import { ChangeDetectionStrategy, Component, computed, effect, signal, untracked, viewChild } from '@angular/core';
import { HD_BODYGRAPH_GATES, HD_BODYGRAPH_GATE_TO_CENTER } from '@/app/human-design/chart/bodygraph/hd-bodygraph-metadata';
import { HdBodygraphCenter } from '@/app/human-design/chart/bodygraph/hd-bodygraph.models';
import {
  HdCheatSheetGridComponent,
  HdCheatSheetPaintValidation,
  validateHdCheatSheetPaintCenter,
} from '@/app/human-design/chart/bodygraph/gate-info/cheat-sheet-grid/hd-cheat-sheet-grid.component';
import {
  HD_BODYGRAPH_COLOR_CONFIG_PRESETS,
  HdBodygraphColorConfig,
  HdBodygraphColorConfigPresetId,
  HD_BODYGRAPH_WARM_DUSK_SUBTLE_COLOR_CONFIG,
} from '@/app/human-design/chart/bodygraph/hd-bodygraph-color-config';
import {
  HdBodygraphIconKey,
  getHdGateIconKeys,
} from '@/app/human-design/chart/bodygraph/hd-bodygraph-icons';

/**
 * Pagina die het volledige Human Design Gates Cheat Sheet (alle 64 poorten) toont.
 *
 * Wikkelt {@link HdCheatSheetGridComponent} samen met de
 * {@link HdCheatSheetControlsBarComponent} en orkestreert de stukjes state
 * die niet in de grid zelf horen:
 * - de visibility-toggles (poortcijfer, kleuren, circuit-info, kanaal+pp, keynotes,
 *   body) — model signals op de controls-bar, één-op-één doorgegeven aan de grid;
 * - de gate-volgorde — `signal<readonly number[]>` die de dice-knop opnieuw
 *   schudt via een Fisher-Yates shuffle;
 * - de icoon-filters — multi-select uit de controls-bar; de grid toont enkel
 *   poorten waarvan de icoon-lay-out minstens één geselecteerd icoon bevat;
 * - de centerfilters — multi-select uit de controls-bar; gecombineerd met de
 *   icoon-filters als EN (binnen elke groep OF);
 * - de inkleur-spelmodus — geeft modus en verfemmer door aan de grid (die de
 *   inkleurpogingen afhandelt) en rekent op de Valideer-knop uit hoeveel
 *   poorten van die kleur nog niet onthuld zijn.
 *
 * De per-card reveal-state blijft in de grid (zie `HdCheatSheetGridComponent`)
 * omdat ze pure UI-state is en de pagina daar geen verdere logica op heeft.
 */
@Component({
  selector: 'hd-cheat-sheet-page',
  standalone: false,
  templateUrl: './hd-cheat-sheet-page.component.html',
  styleUrl: './hd-cheat-sheet-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdCheatSheetPageComponent {
  protected readonly hideGateNumber = signal(false);
  protected readonly hideKeynotes = signal(false);
  protected readonly hideColors = signal(false);
  protected readonly hideFooterCircuit = signal(false);
  protected readonly hideFooterChannel = signal(false);
  protected readonly hideBody = signal(false);
  protected readonly bodyView = signal<'default' | 'lines'>('default');
  protected readonly colorConfigId = signal<HdBodygraphColorConfigPresetId>('warm-dusk-subtle');
  protected readonly colorConfig = computed<HdBodygraphColorConfig>(
    () => HD_BODYGRAPH_COLOR_CONFIG_PRESETS.find(p => p.id === this.colorConfigId())?.config
      ?? HD_BODYGRAPH_WARM_DUSK_SUBTLE_COLOR_CONFIG,
  );

  protected readonly iconFilters = signal<readonly HdBodygraphIconKey[]>([]);
  protected readonly centerFilters = signal<readonly HdBodygraphCenter[]>([]);

  /** Inkleur-spelmodus (zie `HdCheatSheetGridComponent`); de controls-bar zet de kleuren dan uit. */
  protected readonly paintMode = signal(false);
  protected readonly paintCenter = signal<HdBodygraphCenter | null>(null);
  /** Uitkomst van de laatste Valideer-klik; verdwijnt zodra de verfemmer of de modus wisselt of er opnieuw gekleurd wordt. */
  protected readonly paintValidation = signal<HdCheatSheetPaintValidation | null>(null);

  private readonly grid = viewChild(HdCheatSheetGridComponent);

  constructor() {
    effect(() => {
      this.paintCenter();
      this.paintMode();
      untracked(() => this.paintValidation.set(null));
    });
  }

  /** Valideer-knop: de gekozen kleur, of zonder kleur alle 64 poorten. */
  protected onValidatePaint(): void {
    const grid = this.grid();
    if (grid == null) {
      return;
    }
    this.paintValidation.set(validateHdCheatSheetPaintCenter(this.paintCenter(), grid.revealedGates()));
  }

  /** Na elke volgende inkleurpoging is de uitkomst verouderd en verdwijnt ze. */
  protected onPaintAttempted(): void {
    this.paintValidation.set(null);
  }

  /** Volledige (geschudde) volgorde; filtering gebeurt in {@link gates}. */
  private readonly gateOrder = signal<readonly number[]>(HD_BODYGRAPH_GATES);

  /**
   * Zichtbare poorten: de volgorde, gefilterd op de gekozen centers (OF) én op
   * de geselecteerde iconen (OF); een lege groep filtert niet.
   */
  protected readonly gates = computed<readonly number[]>(() => {
    const iconFilters = this.iconFilters();
    const centerFilters = this.centerFilters();
    const order = this.gateOrder();
    if (iconFilters.length === 0 && centerFilters.length === 0) {
      return order;
    }
    return order.filter(gate => {
      if (centerFilters.length > 0 && !centerFilters.includes(HD_BODYGRAPH_GATE_TO_CENTER[gate])) {
        return false;
      }
      if (iconFilters.length === 0) {
        return true;
      }
      const iconKeys = getHdGateIconKeys(gate);
      return iconFilters.some(filter => iconKeys.includes(filter));
    });
  });

  protected onShuffleGates(): void {
    this.gateOrder.set(shuffleArray(this.gateOrder()));
  }
}

/**
 * Fisher-Yates shuffle die een nieuwe array teruggeeft (input blijft onaangeroerd).
 * Gebruikt een tijdelijke kopie zodat het signal correct triggert (referentie-equality).
 */
function shuffleArray<T>(items: readonly T[]): T[] {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}
